import React, { useMemo, useState } from "react";
import useSWR from "swr";
import api, { money, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DateInputES } from "@/components/ui/DateInputES";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import GenderIcon from "@/components/GenderIcon";
import {
  Plus, BedDouble, AlertTriangle, GripVertical, X, Users, Search, Wand2, Building2, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { ROOM_TYPE, ACC_STATUS } from "@/lib/labels";
import {
  DndContext, DragOverlay, PointerSensor, KeyboardSensor, TouchSensor,
  useSensor, useSensors, useDraggable, useDroppable, closestCenter,
} from "@dnd-kit/core";

const fetcher = (u) => api.get(u).then((r) => r.data);
const emptyAcc = {
  tour_ids: [], date: "", city: "", hotel_name: "", nights: 1,
  price: 0, rooms_count: 1, currency: "USD", confirmation_status: "in_progress",
  check_in: "", check_out: "",
};
const ROOM_CAP = { single: 1, double: 2, twin: 2, triple: 3, quad: 4, quintuple: 5, suite: 4, apartment: 6 };

// ---------------------------------------------------------------------------
// Draggable participant chip (dnd-kit)
// ---------------------------------------------------------------------------
function ParticipantChip({ participant, containerId, onRemove, compact = false }) {
  const dragId = `p:${participant.id}:${containerId}`;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: dragId,
    data: { participantId: participant.id, from: containerId },
  });
  const rt = ROOM_TYPE[participant.room_type] || participant.room_type || "Doble";
  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-testid={`chip-${participant.id}`}
      className={`group inline-flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-full border text-sm shadow-sm cursor-grab active:cursor-grabbing touch-none select-none transition-all
        ${isDragging
          ? "opacity-30 border-[#026FCB]"
          : "bg-white dark:bg-[#0B0F17] border-slate-200 dark:border-[#25334D] hover:border-[#026FCB] hover:shadow-md"}
      `}
      style={{ userSelect: "none" }}
    >
      <GripVertical size={12} className="text-slate-400 group-hover:text-[#026FCB]" />
      <GenderIcon gender={participant.gender} size={10} />
      <span className={`truncate ${compact ? "max-w-[110px]" : "max-w-[160px]"} font-medium`}>
        {participant.first_name} {participant.last_name}
      </span>
      <span
        className={`text-[10px] mono uppercase tracking-wider px-1.5 py-0.5 rounded
          ${participant.room_type === "single"
            ? "bg-purple-100 text-purple-700 dark:bg-purple-950 dark:text-purple-300"
            : participant.room_type === "twin"
            ? "bg-teal-100 text-teal-700 dark:bg-teal-950 dark:text-teal-300"
            : "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300"}`}
      >
        {rt}
      </span>
      {onRemove && (
        <button
          onPointerDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="ml-0.5 text-slate-400 hover:text-red-500 transition-colors"
          data-testid={`rm-${participant.id}`}
          title="Quitar de habitación"
        >
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Room card = droppable + list of participant chips
// ---------------------------------------------------------------------------
function RoomCard({ room, participants, activeParticipant, onEdit, onDelete, onRemoveParticipant }) {
  const cap = ROOM_CAP[room.room_type] || 2;
  const occ = (room.participant_ids || []).length;
  const overCap = occ > cap;
  const roomOccupants = (room.participant_ids || [])
    .map((pid) => participants.find((x) => x.id === pid))
    .filter(Boolean);

  const { isOver, setNodeRef } = useDroppable({
    id: `room:${room.id}`,
    data: { kind: "room", roomId: room.id, roomType: room.room_type, cap, occ },
  });

  // Determine if the drop would exceed capacity or mismatch preferred type
  const willOverfill = activeParticipant && isOver && !room.participant_ids.includes(activeParticipant.id) && occ + 1 > cap;
  const typeMismatch = activeParticipant && isOver && activeParticipant.room_type && activeParticipant.room_type !== room.room_type;

  return (
    <div
      ref={setNodeRef}
      data-testid={`room-${room.id}`}
      className={`relative rounded-xl p-4 border-2 transition-all
        ${isOver
          ? willOverfill
            ? "border-red-500 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-500/40"
            : typeMismatch
            ? "border-amber-500 bg-amber-50 dark:bg-amber-950/30 ring-2 ring-amber-500/30"
            : "border-[#026FCB] bg-sky-50 dark:bg-sky-950/40 ring-2 ring-[#026FCB]/30"
          : overCap
          ? "border-red-400 bg-red-50/30 dark:bg-red-950/10"
          : "border-dashed border-slate-300 dark:border-[#25334D] hover:border-slate-400"}
      `}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="min-w-0">
          <div className="font-semibold truncate flex items-center gap-2">
            <BedDouble size={14} className="text-[#026FCB] shrink-0" />
            {room.room_label}
          </div>
          <div className="text-[11px] mono uppercase tracking-wider text-slate-500 mt-0.5">
            {ROOM_TYPE[room.room_type] || room.room_type} · <span className={overCap ? "text-red-600 font-bold" : ""}>{occ}/{cap}</span>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {overCap && <StatusBadge value="cancelled" label="Sobreocupada" />}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-[#026FCB]"
            onClick={() => onEdit(room)}
            data-testid={`edit-room-${room.id}`}
            title="Editar habitación"
          >
            <Pencil size={14} />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-slate-400 hover:text-red-500"
            onClick={() => onDelete(room.id)}
            data-testid={`del-room-${room.id}`}
            title="Eliminar habitación"
          >
            <X size={14} />
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 min-h-[44px]">
        {roomOccupants.map((p) => (
          <ParticipantChip
            key={p.id}
            participant={p}
            containerId={`room:${room.id}`}
            onRemove={() => onRemoveParticipant(p.id)}
          />
        ))}
        {roomOccupants.length === 0 && (
          <div className="text-xs text-slate-400 mono uppercase tracking-wider py-2">
            {isOver ? "Suelta aquí" : "Arrastra aquí"}
          </div>
        )}
      </div>

      {isOver && typeMismatch && !willOverfill && (
        <div className="absolute -bottom-2 left-3 text-[10px] mono uppercase bg-amber-500 text-white px-2 py-0.5 rounded shadow">
          Tipo distinto ({ROOM_TYPE[activeParticipant.room_type]})
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Unassigned zone = droppable pool of participants without room
// ---------------------------------------------------------------------------
function UnassignedZone({ participants }) {
  const { isOver, setNodeRef } = useDroppable({
    id: "unassigned",
    data: { kind: "unassigned" },
  });
  return (
    <div
      ref={setNodeRef}
      data-testid="unassigned-zone"
      className={`rounded-xl border-2 p-4 min-h-[260px] transition-all
        ${isOver
          ? "border-red-400 bg-red-50 dark:bg-red-950/30 ring-2 ring-red-400/30"
          : "border-dashed border-slate-300 dark:border-[#25334D]"}
      `}
    >
      <div className="flex items-center gap-2 text-sm font-semibold mb-3">
        <Users size={16} />
        Sin habitación
        <span className="ml-1 text-xs mono bg-slate-100 dark:bg-[#0B0F17] px-1.5 py-0.5 rounded">
          {participants.length}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {participants.map((p) => (
          <ParticipantChip key={p.id} participant={p} containerId="unassigned" />
        ))}
        {participants.length === 0 && (
          <div className="text-xs text-slate-400 mono uppercase tracking-wider py-4">
            Todos asignados
          </div>
        )}
      </div>
      <p className="text-[11px] text-slate-500 mt-4 leading-relaxed">
        Suelta aquí para quitar de una habitación. Arrastra desde aquí para asignar.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export default function Accommodations() {
  const { data: tours } = useSWR("/tours", fetcher);
  const [selTour, setSelTour] = useState("");
  const { data: accs, mutate } = useSWR(selTour ? `/accommodations?tour_id=${selTour}` : null, fetcher);
  const { data: rooming, mutate: mutateRoom } = useSWR(
    selTour ? `/rooming-list/${selTour}` : null,
    fetcher,
  );

  const [openAcc, setOpenAcc] = useState(false);
  const [accForm, setAccForm] = useState(emptyAcc);
  const [editAccOpen, setEditAccOpen] = useState(false);
  const [editAccForm, setEditAccForm] = useState(emptyAcc);
  const [editAccId, setEditAccId] = useState(null);
  const [openRoom, setOpenRoom] = useState(false);
  const [roomForm, setRoomForm] = useState({ accommodation_id: "", room_label: "", room_type: "double" });

  // Edit room dialog
  const [editRoomOpen, setEditRoomOpen] = useState(false);
  const [editRoomForm, setEditRoomForm] = useState({ room_label: "", room_type: "double" });
  const [editRoomId, setEditRoomId] = useState(null);

  const [activeId, setActiveId] = useState(null);
  const [search, setSearch] = useState("");

  React.useEffect(() => {
    if (tours && !selTour && tours[0]) setSelTour(tours[0].id);
  }, [tours, selTour]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 100, tolerance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const participants = rooming?.participants || [];
  const assignments = rooming?.assignments || [];

  // Filter unassigned by search
  const filteredUnassigned = useMemo(() => {
    const u = rooming?.unassigned || [];
    if (!search.trim()) return u;
    const q = search.toLowerCase();
    return u.filter((p) =>
      `${p.first_name} ${p.last_name} ${p.email}`.toLowerCase().includes(q),
    );
  }, [rooming, search]);

  const activeParticipant = useMemo(() => {
    if (!activeId) return null;
    const [, pid] = String(activeId).split(":");
    return participants.find((p) => p.id === pid) || null;
  }, [activeId, participants]);

  // Optimistic move + persist
  const move = async (participantId, targetRoomId) => {
    const optimistic = {
      ...rooming,
      assignments: assignments.map((a) => ({
        ...a,
        participant_ids: (a.participant_ids || []).filter((x) => x !== participantId),
      })).map((a) =>
        a.id === targetRoomId
          ? { ...a, participant_ids: [...(a.participant_ids || []), participantId] }
          : a,
      ),
    };
    // recompute unassigned
    const assignedIds = new Set(
      optimistic.assignments.flatMap((a) => a.participant_ids || []),
    );
    optimistic.unassigned = participants.filter(
      (p) =>
        p.booking_status !== "cancelled" &&
        p.booking_status !== "refunded" &&
        !assignedIds.has(p.id),
    );
    mutateRoom(optimistic, false);
    try {
      await api.post("/rooming/move", {
        participant_id: participantId,
        target_assignment_id: targetRoomId,
      });
      mutateRoom();
    } catch (e) {
      toast.error("No se pudo mover");
      mutateRoom();
    }
  };

  const unassign = async (participantId) => {
    const optimistic = {
      ...rooming,
      assignments: assignments.map((a) => ({
        ...a,
        participant_ids: (a.participant_ids || []).filter((x) => x !== participantId),
      })),
    };
    const assignedIds = new Set(
      optimistic.assignments.flatMap((a) => a.participant_ids || []),
    );
    optimistic.unassigned = participants.filter(
      (p) =>
        p.booking_status !== "cancelled" &&
        p.booking_status !== "refunded" &&
        !assignedIds.has(p.id),
    );
    mutateRoom(optimistic, false);
    try {
      await api.post("/rooming/move", {
        participant_id: participantId,
        target_assignment_id: null,
      });
      mutateRoom();
    } catch (e) {
      toast.error("No se pudo mover");
      mutateRoom();
    }
  };

  const onDragStart = (event) => setActiveId(event.active.id);
  const onDragCancel = () => setActiveId(null);
  const onDragEnd = (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over) return;
    const { participantId, from } = active.data.current || {};
    if (!participantId) return;

    if (over.id === "unassigned") {
      if (from !== "unassigned") unassign(participantId);
      return;
    }
    const overData = over.data.current;
    if (overData?.kind === "room") {
      const currentRoom = assignments.find((a) => (a.participant_ids || []).includes(participantId));
      if (currentRoom && currentRoom.id === overData.roomId) return; // no-op

      const cap = ROOM_CAP[overData.roomType] || 2;
      if ((overData.occ || 0) + 1 > cap) {
        // still allow, but warn — backend accepts it, UI marks it "Sobreocupada"
        toast.warning(`Habitación llena (${cap} plazas). Se marcará como sobreocupada.`);
      }
      move(participantId, overData.roomId);
    }
  };

  // Auto-assign by preferred room_type — greedy fill
  const autoAssign = async () => {
    if (!rooming) return;
    const pool = [...(rooming.unassigned || [])];
    if (pool.length === 0) return toast.info("No hay participantes sin habitación");
    // build capacity map
    const roomState = assignments.map((r) => ({
      id: r.id,
      type: r.room_type,
      cap: ROOM_CAP[r.room_type] || 2,
      occ: (r.participant_ids || []).length,
    }));
    const moves = [];
    for (const p of pool) {
      // prefer same room_type
      let target = roomState.find((r) => r.type === p.room_type && r.occ < r.cap);
      // fallback: any with space
      if (!target) target = roomState.find((r) => r.occ < r.cap);
      if (!target) break;
      target.occ += 1;
      moves.push({ pid: p.id, rid: target.id });
    }
    if (moves.length === 0) return toast.warning("No hay habitaciones con espacio");
    try {
      await Promise.all(
        moves.map((m) =>
          api.post("/rooming/move", { participant_id: m.pid, target_assignment_id: m.rid }),
        ),
      );
      toast.success(`${moves.length} participante(s) asignado(s)`);
      mutateRoom();
    } catch {
      toast.error("Error asignando automáticamente");
      mutateRoom();
    }
  };

  // Group rooms by hotel
  const roomsByHotel = useMemo(() => {
    const grouped = {};
    (accs || []).forEach((a) => { grouped[a.id] = { acc: a, rooms: [] }; });
    assignments.forEach((r) => {
      if (grouped[r.accommodation_id]) grouped[r.accommodation_id].rooms.push(r);
    });
    return Object.values(grouped);
  }, [accs, assignments]);

  const submitAcc = async () => {
    // Resolver a qué tours se asigna: los del multi-select o, si vacío, el seleccionado arriba
    const targets = (accForm.tour_ids && accForm.tour_ids.length > 0)
      ? accForm.tour_ids
      : (selTour ? [selTour] : []);
    if (targets.length === 0) return toast.error("Selecciona al menos un tour");
    if (!accForm.hotel_name?.trim()) return toast.error("Falta el nombre del hotel");
    try {
      const { tour_ids, ...rest } = accForm;
      const payload = {
        ...rest,
        tour_ids: targets,
        nights: Number(accForm.nights) || 1,
        rooms_count: Number(accForm.rooms_count) || 1,
        price: Number(accForm.price) || 0,
      };
      const { data } = await api.post("/accommodations/multi", payload);
      toast.success(
        data.count > 1
          ? `Hotel asignado a ${data.count} tours`
          : "Hotel añadido",
      );
      setAccForm(emptyAcc);
      setOpenAcc(false);
      mutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const createRoom = async () => {
    if (!roomForm.accommodation_id || !roomForm.room_label)
      return toast.error("Falta habitación o hotel");
    await api.post("/rooming/room", roomForm);
    setRoomForm({ accommodation_id: "", room_label: "", room_type: "double" });
    setOpenRoom(false);
    mutateRoom();
    toast.success("Habitación creada");
  };

  const deleteRoom = async (rid) => {
    if (!confirm("¿Eliminar habitación?")) return;
    await api.delete(`/rooming/room/${rid}`);
    mutateRoom();
  };

  const openEditRoom = (room) => {
    setEditRoomId(room.id);
    setEditRoomForm({ room_label: room.room_label || "", room_type: room.room_type || "double" });
    setEditRoomOpen(true);
  };

  const saveEditRoom = async () => {
    if (!editRoomId) return;
    if (!editRoomForm.room_label?.trim()) return toast.error("Falta la etiqueta");
    try {
      await api.patch(`/rooming/room/${editRoomId}`, editRoomForm);
      toast.success("Habitación actualizada");
      setEditRoomOpen(false);
      setEditRoomId(null);
      mutateRoom();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const openEditAcc = (a) => {
    setEditAccId(a.id);
    setEditAccForm({
      hotel_name: a.hotel_name || "",
      city: a.city || "",
      date: a.date || "",
      check_in: a.check_in || "",
      check_out: a.check_out || "",
      nights: a.nights ?? 1,
      rooms_count: a.rooms_count ?? 1,
      price: a.price ?? 0,
      currency: a.currency || "USD",
      confirmation_status: a.confirmation_status || "in_progress",
      free_cancellation_until: a.free_cancellation_until || "",
    });
    setEditAccOpen(true);
  };

  const saveEditAcc = async () => {
    if (!editAccId) return;
    try {
      await api.patch(`/accommodations/${editAccId}`, {
        ...editAccForm,
        nights: Number(editAccForm.nights) || 1,
        rooms_count: Number(editAccForm.rooms_count) || 1,
        price: Number(editAccForm.price) || 0,
      });
      toast.success("Hotel actualizado");
      setEditAccOpen(false);
      setEditAccId(null);
      mutate();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Hoteles</h1>
          <p className="text-slate-500 mt-1">
            Gestiona los hoteles y sus habitaciones por tour.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <Select value={selTour} onValueChange={setSelTour}>
            <SelectTrigger className="w-64" data-testid="acc-tour-select">
              <SelectValue placeholder="Selecciona tour" />
            </SelectTrigger>
            <SelectContent>
              {(tours || []).map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Dialog open={openAcc} onOpenChange={setOpenAcc}>
            <DialogTrigger asChild>
              <Button
                data-testid="new-acc-btn"
                className="bg-[#026FCB] hover:bg-[#0358a5]"
              >
                <Plus size={16} className="mr-2" />Nuevo hotel
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nuevo alojamiento</DialogTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Crea un hotel y asígnalo a uno o varios tours. Si no seleccionas ninguno, se asignará al tour activo.
                </p>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Asignar a tour(s)</Label>
                  <div className="mt-1 border border-slate-200 dark:border-[#25334D] rounded-lg p-2 max-h-40 overflow-y-auto space-y-1" data-testid="acc-tours-multi">
                    {(tours || []).map((t) => {
                      const checked = (accForm.tour_ids || []).includes(t.id);
                      return (
                        <label
                          key={t.id}
                          className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer transition-colors ${
                            checked ? "bg-[#026FCB]/10" : "hover:bg-slate-50 dark:hover:bg-[#1A2336]"
                          }`}
                        >
                          <input
                            type="checkbox"
                            data-testid={`acc-tour-cb-${t.id}`}
                            checked={checked}
                            onChange={(e) => {
                              const set = new Set(accForm.tour_ids || []);
                              if (e.target.checked) set.add(t.id); else set.delete(t.id);
                              setAccForm({ ...accForm, tour_ids: Array.from(set) });
                            }}
                            className="accent-[#026FCB]"
                          />
                          <span className="text-sm truncate">{t.name}</span>
                          <span className="text-[10px] mono uppercase text-slate-400 ml-auto">{t.country}</span>
                        </label>
                      );
                    })}
                    {(!tours || tours.length === 0) && (
                      <div className="text-xs text-slate-500 px-2 py-3">No hay tours disponibles.</div>
                    )}
                  </div>
                  {(accForm.tour_ids?.length || 0) > 0 ? (
                    <div className="text-[11px] mono uppercase tracking-wider text-[#026FCB] mt-1">
                      {accForm.tour_ids.length} tour(s) seleccionado(s)
                    </div>
                  ) : (
                    <div className="text-[11px] mono uppercase tracking-wider text-slate-400 mt-1">
                      Se usará el tour activo por defecto
                    </div>
                  )}
                </div>
                <div className="col-span-2">
                  <Label>Hotel</Label>
                  <Input data-testid="acc-hotel-name" value={accForm.hotel_name} onChange={(e) => setAccForm({ ...accForm, hotel_name: e.target.value })} />
                </div>
                <div>
                  <Label>Ciudad</Label>
                  <Input value={accForm.city} onChange={(e) => setAccForm({ ...accForm, city: e.target.value })} />
                </div>
                <div>
                  <Label>Fecha</Label>
                  <DateInputES value={accForm.date} onChange={(e) => setAccForm({ ...accForm, date: e.target.value })} />
                </div>
                <div>
                  <Label>Check-in</Label>
                  <DateInputES value={accForm.check_in} onChange={(e) => setAccForm({ ...accForm, check_in: e.target.value })} />
                </div>
                <div>
                  <Label>Check-out</Label>
                  <DateInputES value={accForm.check_out} onChange={(e) => setAccForm({ ...accForm, check_out: e.target.value })} />
                </div>
                <div>
                  <Label>Noches</Label>
                  <Input type="number" value={accForm.nights} onChange={(e) => setAccForm({ ...accForm, nights: e.target.value })} />
                </div>
                <div>
                  <Label>Nº habitaciones</Label>
                  <Input
                    type="number"
                    min="1"
                    data-testid="acc-rooms"
                    value={accForm.rooms_count}
                    onChange={(e) => setAccForm({ ...accForm, rooms_count: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Precio por habitación</Label>
                  <Input type="number" data-testid="acc-price" value={accForm.price} onChange={(e) => setAccForm({ ...accForm, price: e.target.value })} />
                </div>
                <div className="col-span-2 rounded-lg bg-slate-50 dark:bg-[#1A2336] px-3 py-2 flex items-center justify-between text-sm">
                  <span className="mono uppercase text-xs text-slate-500 tracking-wider">Total estancia</span>
                  <span className="mono font-semibold text-[#026FCB]">
                    {money((Number(accForm.price) || 0) * (Number(accForm.rooms_count) || 0), accForm.currency)}
                    <span className="text-xs text-slate-400 ml-2">
                      ({Number(accForm.rooms_count) || 0} hab × {money(Number(accForm.price) || 0, accForm.currency)})
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <Label>Estado</Label>
                  <Select value={accForm.confirmation_status} onValueChange={(v) => setAccForm({ ...accForm, confirmation_status: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["draft", "in_progress", "confirmed", "cancelled"].map((s) => (
                        <SelectItem key={s} value={s}>{ACC_STATUS[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenAcc(false)}>Cancelar</Button>
                <Button className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={submitAcc}>Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Edit hotel dialog */}
          <Dialog open={editAccOpen} onOpenChange={setEditAccOpen}>
            <DialogContent data-testid="edit-acc-dialog">
              <DialogHeader><DialogTitle>Editar hotel</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Hotel</Label>
                  <Input value={editAccForm.hotel_name || ""} onChange={(e) => setEditAccForm({ ...editAccForm, hotel_name: e.target.value })} />
                </div>
                <div>
                  <Label>Ciudad</Label>
                  <Input value={editAccForm.city || ""} onChange={(e) => setEditAccForm({ ...editAccForm, city: e.target.value })} />
                </div>
                <div>
                  <Label>Fecha</Label>
                  <DateInputES value={editAccForm.date || ""} onChange={(e) => setEditAccForm({ ...editAccForm, date: e.target.value })} />
                </div>
                <div>
                  <Label>Check-in</Label>
                  <DateInputES value={editAccForm.check_in || ""} onChange={(e) => setEditAccForm({ ...editAccForm, check_in: e.target.value })} />
                </div>
                <div>
                  <Label>Check-out</Label>
                  <DateInputES value={editAccForm.check_out || ""} onChange={(e) => setEditAccForm({ ...editAccForm, check_out: e.target.value })} />
                </div>
                <div>
                  <Label>Noches</Label>
                  <Input type="number" value={editAccForm.nights ?? 1} onChange={(e) => setEditAccForm({ ...editAccForm, nights: e.target.value })} />
                </div>
                <div>
                  <Label>Cancelación gratis hasta</Label>
                  <DateInputES value={editAccForm.free_cancellation_until || ""} onChange={(e) => setEditAccForm({ ...editAccForm, free_cancellation_until: e.target.value })} />
                </div>
                <div>
                  <Label>Nº habitaciones</Label>
                  <Input
                    type="number"
                    min="1"
                    data-testid="edit-acc-rooms"
                    value={editAccForm.rooms_count ?? 1}
                    onChange={(e) => setEditAccForm({ ...editAccForm, rooms_count: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Precio por habitación</Label>
                  <Input
                    type="number"
                    data-testid="edit-acc-price"
                    value={editAccForm.price ?? 0}
                    onChange={(e) => setEditAccForm({ ...editAccForm, price: e.target.value })}
                  />
                </div>
                <div className="col-span-2 rounded-lg bg-slate-50 dark:bg-[#1A2336] px-3 py-2 flex items-center justify-between text-sm">
                  <span className="mono uppercase text-xs text-slate-500 tracking-wider">Total estancia</span>
                  <span className="mono font-semibold text-[#026FCB]">
                    {money((Number(editAccForm.price) || 0) * (Number(editAccForm.rooms_count) || 0), editAccForm.currency)}
                    <span className="text-xs text-slate-400 ml-2">
                      ({Number(editAccForm.rooms_count) || 0} hab × {money(Number(editAccForm.price) || 0, editAccForm.currency)})
                    </span>
                  </span>
                </div>
                <div className="col-span-2">
                  <Label>Estado</Label>
                  <Select value={accForm.confirmation_status} onValueChange={(v) => setAccForm({ ...accForm, confirmation_status: v })}>
                    <SelectTrigger data-testid="edit-acc-status"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["draft", "in_progress", "confirmed"].map((s) => (
                        <SelectItem key={s} value={s}>{ACC_STATUS[s]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditAccOpen(false)}>Cancelar</Button>
                <Button data-testid="edit-acc-save" className="bg-[#026FCB] hover:bg-[#0358a5]" onClick={saveEditAcc}>Guardar cambios</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Hotels summary */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(accs || []).map((a) => {
          const rc = a.rooms_count || 1;
          const total = (a.price || 0) * rc;
          return (
            <Card key={a.id} data-testid={`acc-card-${a.id}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-lg truncate">{a.hotel_name}</div>
                    <div className="text-sm text-slate-500">{a.city} · {a.nights} noches · {rc} hab.</div>
                    <div className="text-xs text-slate-500 mono mt-1">
                      Check-in: {fmtDate(a.check_in)} · Check-out: {fmtDate(a.check_out)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <StatusBadge value={a.confirmation_status} kind="acc" />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 w-8 p-0"
                      data-testid={`edit-acc-${a.id}`}
                      onClick={() => openEditAcc(a)}
                      title="Editar hotel"
                    >
                      <Pencil size={14} />
                    </Button>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <div className="text-xs text-slate-500">
                    Cancelación gratis hasta <span className="mono">{fmtDate(a.free_cancellation_until)}</span>
                  </div>
                  <div className="text-right">
                    <div className="mono font-semibold text-base">{money(total, a.currency)}</div>
                    <div className="text-[10px] mono uppercase text-slate-400 tracking-wider">
                      {rc} × {money(a.price, a.currency)}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        onDragCancel={onDragCancel}
      >
        <DragOverlay dropAnimation={{ duration: 180 }}>
          {activeParticipant ? (
            <div className="inline-flex items-center gap-2 pl-1.5 pr-2 py-1.5 rounded-full bg-white dark:bg-[#0B0F17] border-2 border-[#026FCB] shadow-lg text-sm cursor-grabbing">
              <GripVertical size={12} className="text-[#026FCB]" />
              <GenderIcon gender={activeParticipant.gender} size={10} />
              <span className="font-semibold">
                {activeParticipant.first_name} {activeParticipant.last_name}
              </span>
              <span className="text-[10px] mono uppercase bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300 px-1.5 py-0.5 rounded">
                {ROOM_TYPE[activeParticipant.room_type] || activeParticipant.room_type}
              </span>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}
